"use strict";

import Constants from "/js/core/constants.js";
import Util from "/js/util/util.js";
import AppUtil from "/js/util/app-util.js";
import Global from "/js/core/global.js";
import Metrics from "/js/util/metrics.js";
import ResultView from "/js/top-level-views/interaction-result-view.js";
import InteractionViewUtil from "/js/interaction-views/interaction-view-util.js";
import UserLessons from "/js/core/user-lessons";

/**
 * Top-level view that is responsible for instantiating concrete interactions and coordinating
 * between the interaction and interaction result view.
 */
export default class InteractionHolderView {

  #$el = $('#interactionHolder');
  #$innerHolder = this.#$el.find("#interactionInner");
  #$title = this.#$el.find("#interactionTitle");
  
  #interactionView = null;
  #resultView = null;
  
  #data = null;
  #isOpen = false;

  #tabIndexCache = [];

  constructor() {
    this.#resultView = new ResultView();
    this.#$el.on('interaction-body-finished', (...rest) => this.#onInteractionBodyFinished(...rest));
  }

  get $el() {
    return this.#$el;
  }

  resize() {
    if (!this.#interactionView) {
      return;
    }
    // Same sort of pattern as with `CardsHolderView`
    Util.setScale(this.#$innerHolder, Global.cardScale);

    this.#interactionView.resize();
    this.#resultView.resize();
  }

  /**
   * Launches an interaction.
   *
   * @param data The interaction data that comes from the lesson json
   * @param initialAnswers The user's (previously created) answers to this interaction, if any
   */
  launch(data, initialAnswers) {
    this.#data = data;

    this.#resultView.$el.hide();
    this.#killInteractionView();
    this.#isOpen = true;

    const titleText = data.caption || "";
    this.#$title.text(titleText);
    this.#$title.show();

    this.#interactionView = InteractionViewUtil.makeInteractionView(data, initialAnswers);
    this.#$innerHolder.append(this.#interactionView.$el);

    this.#$el.show();
    this.resize();
    this.#$title.show();
    this.#$title.css("opacity", 1);
    this.#interactionView.show();

    Util.windowUnselect();
  }

  hide(callback) {
    const onComplete = () => {
      this.#killInteractionView();
      this.#$el.hide();
      this.#isOpen = false;
      if (callback) {
        callback();
      }
    }
    const b = this.#resultView.$el.is(':visible');
    if (b) {
      // The resultview is visible, so animate it out.
      this.#resultView.hide(onComplete);
    } else {
      // The interaction itself is visible, so animate it out.
      this.$el.fadeOut(400, onComplete);  // TODO: don't use jquery, for consistency
    }
  }

  get isOpen() {
    return this.#isOpen;
  }

  set isEnabled(b) {
    if (b) {
      Util.setAriaHidden(this.#$el, false);
      AppUtil.restoreTabIndexValues(this.#tabIndexCache);
    } else {
      Util.setAriaHidden(this.#$el, true);
      this.#tabIndexCache = AppUtil.cacheAndClearTabIndexValues(this.#$innerHolder);
    }
  }

  #killInteractionView() {
    this.#isOpen = false;
    if (this.#interactionView) {
      this.#interactionView.$el.remove();
      this.#interactionView = null;
    }
    this.#tabIndexCache = [];
  }

  #onInteractionBodyFinished(e, answers) {
    // Fade out title // TODO: should actually slide up
    this.#$title.fadeOut(Constants.generalAnimDuration);
    // Anim out interaction
    this.#interactionView.hide();
    // Save answers
    if (this.#data.shouldSave && !Global.cmsDevJson) {
      UserLessons.current.answers = answers.data;
    }
    // Either show result, or send done event.
    const shouldShowResultView = this.#data.result
        && this.#data.result.outputs 
        && this.#data.result.outputs.length > 0;
    if (!shouldShowResultView) {
      this.#$el.trigger("interaction-done", this.#data.interactionId);
    } else {
      this.#resultView.show(this.#data, answers.data);
    }
  }
}
